import { Space, Typography, Tag, Row, Select, InputNumber, Col, message, Form, Checkbox, Tooltip } from "antd";
import { SettingOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Fragment, useEffect } from "react";
import { useMemo } from "react";
import useTranslate from "src/utils/useTranslate";
import { useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import "./Objective.scss"
import { useValue } from "src/utils/useValue";
import { tewntypercentPlusMinusRange } from "src/components/AIEngine/Extrapolation/BayesianOptimization/Constraints/Constraints";

const { Text } = Typography;


const columnStyle: React.CSSProperties = {
    borderRight: '1px solid #e8e8e8',
    padding: '8px 12px',
};

const HeaderRow = () => {
    const [t] = useTranslate();
    return (
        <Row style={{ borderBottom: '1px solid #e8e8e8', background: '#fafafa' }}>
            <Col span={6} style={columnStyle}><Text strong>{t("extrapolation.objective")}</Text></Col>
            <Col span={3} style={columnStyle}><Text strong>{t("extrapolation.objective.baseInputRange")}</Text></Col>
            <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.optimisationType")}</Text></Col>
            <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.minValue")}</Text></Col>
            <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.maxValue")}</Text></Col>
            <Col span={3} style={columnStyle}><Text strong>{t("extrapolation.objective.priority")}</Text></Col> {/* Last one no border */}
        </Row>)
};

const optimisationType = [{ id: 1, name: 'Minimize' }, { id: 2, name: 'Maximize' }, { id: 3, name: 'Range' }];

const convertToLabelValue = (data: any, key: string | number, value: string | number) => data.map((item: any) => ({ label: item[key], value: item[value] }));

type ObjectiveItem = { id: number;[key: string]: any };
type DataTypeKey = 'ingredients' | 'processing' | 'properties' | 'formulations';
interface ObjectiveProps {
    rows: ObjectiveItem[];
    setRows: React.Dispatch<React.SetStateAction<ObjectiveItem[]>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>
    userIgnoredData: Record<DataTypeKey, string[]>
    propertiesDataList: any;
    setPropertiesDataList: React.Dispatch<React.SetStateAction<any>>;
    trialInputsData: Record<DataTypeKey, string[]>;
    isAllObjectivesSelected:boolean;
    setIsAllObjectivesSelected: React.Dispatch<React.SetStateAction<boolean>>;

}
const Objective: React.FC<ObjectiveProps> = (props: ObjectiveProps) => {
    const { rows, setRows, setOpen, setUnsavedChanges, userIgnoredData, propertiesDataList, setPropertiesDataList, trialInputsData, isAllObjectivesSelected, setIsAllObjectivesSelected } = props;
    const [t] = useTranslate();
    const { convertValue, parser, formatter } = useValue();
    const { propertiesData } = useSelector((state: StoreState) => state.extrapolation)

    const { properties } = useSelector((state: StoreState) => state.displayNames?.data);

    useEffect(() => {
        let arr: { name: string; baseStartRange: number; baseEndRange: number; id: number; key: any; identifier?: string }[] = [];
        if (
            Object.keys(propertiesData || {}).length > 0
        ) {

            Object.entries(propertiesData?.Properties || {})?.forEach(([key, value]: any, index: number) => {
                const meta = properties?.[key];
                if (meta && trialInputsData?.properties?.includes(key) && !userIgnoredData?.properties?.includes(key) && !Array.isArray(value)) {
                    arr?.push({
                        key,
                        name: meta.name,
                        identifier: meta.identifier,
                        baseStartRange: value.min,
                        baseEndRange: value.max,
                        id: index
                    });
                }
            });

            setPropertiesDataList(arr);
        }
    }, [propertiesData, properties, userIgnoredData?.properties, setPropertiesDataList, trialInputsData?.properties]);



    const addRow = () => {
        const id = Date.now();
        setRows(prev => [...prev, { id, errMsg: "", error: false, errorType: "" }]);

    };

    const deleteRow = (id: number) => {
        setRows(prev => prev?.filter(row => row.id !== id));
        // set all objectives selected to false when a row is deleted
        setIsAllObjectivesSelected(false);

    };

    const objectiveOptions = useMemo(() => convertToLabelValue(propertiesDataList.filter((item: any) => !rows.some((row: any) => row?.key === item?.key)), 'name', 'name'), [propertiesDataList, rows]);
    const optimisationOptions = useMemo(() => convertToLabelValue(optimisationType, 'name', 'name'), []);
    const priorityOptions = useMemo(() => convertToLabelValue(Array.from({ length: rows.filter(row => row?.optimisationType === "Maximize" || row?.optimisationType === "Minimize").length }, (_, i) => ({ id: i + 1, name: i + 1 })), 'name', 'name'), [rows]);

    const updateObjectiveData = (id: number, key: string, value: any) => {

        setUnsavedChanges(true);
        switch (key) {

            case "objective": {
                const selected = propertiesDataList?.find(
                    (obj: { name: any }) => obj.name === value
                );
                if (!selected) return;
                if (rows?.some(row => row?.key === selected?.key)) {
                    message.error(t("extrapolation.bayesianOptimization.duplicateObjective"));
                    return;
                }
                // when selected all objective, mark select all to true
                if(rows.length  === propertiesDataList?.length) {
                    setIsAllObjectivesSelected(true)
                }
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id === id) {
                            delete obj?.priority;
                            delete obj?.optimisationType;
                            delete obj?.minValue;
                            delete obj?.maxValue;
                            if (obj?.errorType === key) {
                                obj.error = false;
                                obj.errorType = "";
                                obj.errMsg = "";
                            }
                            return {
                                ...obj,
                                [key]: value,
                                startRange: selected?.baseStartRange,
                                endRange: selected?.baseEndRange,
                                key: selected?.key,
                            }
                        }
                        return obj;
                    })

                );
                break;
            }
            case "minValue":
            case "maxValue":
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id !== id) return obj;

                        // Create a copy of the object
                        const updated = { ...obj };

                        if (value === null || value === undefined) {
                            // Remove the key
                            delete updated?.[key];
                        } else {
                            // Set the key
                            updated[key] = convertValue(value);
                            if (updated?.errorType === key) {
                                updated.error = false;
                                updated.errorType = "";
                                updated.errMsg = "";
                            }
                        }

                        return updated;
                    })
                );
                break;
            case "priority":
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id !== id) return obj;

                        // Create a copy of the object
                        const updated = { ...obj };

                        if (value === null || value === undefined) {
                            // Remove the key
                            delete updated?.[key];
                        } else {
                            // Set the key
                            updated[key] = value;
                            if (updated?.errorType === key) {
                                updated.error = false;
                                updated.errorType = "";
                                updated.errMsg = "";
                            }
                        }

                        return updated;
                    })
                );
                break;
            case "optimisationType":
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id !== id) return obj;

                        // Create a copy of the object
                        const updated = { ...obj };

                        if (value === null || value === undefined) {
                            // Remove the key
                            delete updated?.[key];
                        } else {
                            // Set the key
                            updated[key] = value;
                            let findMaxorMinExist = rows?.some((data)=>data.optimisationType === "Maximize" || data.optimisationType === "Minimize")
                            if(!findMaxorMinExist && (value === "Maximize" || value === "Minimize")){
                                // set priority 1 by default for first selction in objectives
                                updated['priority'] = 1
                            }else if(value==="Range"){
                                delete updated?.['priority']
                            }
                            if (updated?.errorType === key) {
                                updated.error = false;
                                updated.errorType = "";
                                updated.errMsg = "";
                            }
                        }
                        delete updated?.['minValue'];
                        delete updated?.['maxValue'];

                        return updated;
                    })
                );
                break;
            default:
                break;
        }
    };

    // select all objectives checkbox
    const handleSelectAllObjectives=(checked: boolean) => {
        setIsAllObjectivesSelected(checked);
        
        if(checked){
            let allObjectivesList = propertiesDataList.map((item: any, index: number) => {
                return {
                id: Date.now() + index, // Generate unique ID for each item
                errMsg:"",
                error: false,
                errorType: "",
                objective: item.name,
                startRange: item.baseStartRange,
                endRange: item.baseEndRange,
                key: item.key
             }});
            setRows(allObjectivesList); 
        }
        else {
            // initial State of objective
            setRows([{ id: Date.now(), error: false, errorType: "", errMsg: "" }]);
        }
    }

    // Add this function for minValue blur validation
    const handleBlur = (row: any, type: string) => {
        if (type === 'minValue') {
            if (row.maxValue === null || row.maxValue === undefined) {
                return
            }
            if (row.maxValue !== null && row.maxValue !== undefined && row.minValue !== null && row.minValue !== undefined && row.minValue > row.maxValue) {
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id !== row.id) return obj;
                        return { ...obj, error: true, errorType: "minValue", errMsg: t("extrapolation.objective.minLessThanMax") };
                    })
                );
            }
        }
        else if (type === 'maxValue') {
            if (row.minValue === null || row.minValue === undefined) {
                return
            }
            if (row.minValue !== null && row.minValue !== undefined && row.maxValue !== null && row.maxValue !== undefined && row.minValue > row.maxValue) {
                setRows(prev =>
                    prev.map(obj => {
                        if (obj?.id !== row.id) return obj;
                        return { ...obj, error: true, errorType: "maxValue", errMsg: t("extrapolation.objective.maxLessThanMin") };
                    })
                );
            }
        }
    };

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Tag
          onClick={() => setOpen(true)}
          style={{ cursor: "pointer", float: "right", marginTop: -32 }}
          icon={<SettingOutlined style={{ margin: 0 }} />}
        >
          {t("extarpolation.objective.configuration")}
        </Tag>
        <Space direction="horizontal" size="middle">
          <Text>{t("extrapolation.objective.selectObjectives")}</Text>

          <Tag
            onClick={addRow}
            icon={<PlusOutlined style={{ margin: 0 }} />}
            className={
              rows?.length >= propertiesDataList?.length
                ? "tag-disabled"
                : "tag-clickable"
            }
            style={{
              cursor:
                rows?.length >= propertiesDataList?.length
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {t("extrapolation.objective.addProperties")}
          </Tag>
        </Space>
        <HeaderRow />
        <div
          style={{
            minHeight: "30vh",
            maxHeight: "30vh",
            overflowY: "auto",
            paddingRight: 8,
          }}
        >
          {rows.map((row) => {
            const minWarn =
              row?.minValue <
              convertValue(
                tewntypercentPlusMinusRange(row?.startRange, row?.endRange).min
              );

            const maxWarn =
              row?.maxValue >
              convertValue(
                tewntypercentPlusMinusRange(row?.startRange, row?.endRange).max
              );

            return (
              <Row
                key={row.id}
                gutter={16}
                align="middle"
                style={{
                  alignItems: "flex-start",
                  marginBottom: 8,
                }}
              >
                {/* Objective */}
                <Col
                  span={6}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: 10,
                  }}
                >
                  <DeleteOutlined
                    onClick={() => deleteRow(row.id)}
                    style={{ marginLeft: 8, cursor: "pointer", marginTop: -22 }}
                  />
                  <Form.Item
                    style={{ marginBottom: 0, flex: 1, minWidth: "90%" }}
                    validateStatus={
                      row?.error && row?.errorType === "objective"
                        ? "error"
                        : ""
                    }
                    // unified fixed-height slot
                    help={
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 2,
                          minHeight: 20,
                          color:
                            row?.error && row?.errorType === "objective"
                              ? "red"
                              : "transparent",
                        }}
                      >
                        {row?.error && row?.errorType === "objective"
                          ? row?.errMsg
                          : "\u00A0"}
                      </div>
                    }
                  >
                    <Select
                      value={row?.objective ?? ""}
                      showSearch
                      filterOption={(input, option) =>
                        ((option as any)?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(val) =>
                        updateObjectiveData(row.id, "objective", val)
                      }
                      notFoundContent={
                        <Space
                          style={{ background: "whitesmoke", width: "100%" }}
                        >
                          <Text>
                            {isAllObjectivesSelected
                              ? "You have selected all the parameters"
                              : !propertiesDataList.length
                              ? "NO OBJECTIVE FOUND"
                              : ""}
                          </Text>
                        </Space>
                      }
                      placeholder="Select type"
                      options={objectiveOptions}
                      style={{ width: "100%" }}
                      dropdownRender={(menu) => (
                        <>
                          {!!propertiesDataList?.length && (
                            <div
                              style={{
                                padding: 8,
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              <Checkbox
                                checked={isAllObjectivesSelected}
                                onChange={(e) =>
                                  handleSelectAllObjectives(e.target.checked)
                                }
                              >
                                {t("common.selectAll")}
                              </Checkbox>
                            </div>
                          )}
                          {menu}
                        </>
                      )}
                    />
                  </Form.Item>
                </Col>

                {/* Base range */}
                <Col span={3} style={{ paddingLeft: 20, paddingTop: 5, whiteSpace:"normal",wordBreak:"break-all" }}>
                  {convertValue(row?.startRange)} -{" "}
                  {convertValue(row?.endRange)}
                </Col>

                {/* Optimisation Type */}
                <Col span={4}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={
                      row?.error && row?.errorType === "optimisationType"
                        ? "error"
                        : ""
                    }
                    help={
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 2,
                          minHeight: 20,
                          color:
                            row?.error && row?.errorType === "optimisationType"
                              ? "red"
                              : "transparent",
                        }}
                      >
                        {row?.error && row?.errorType === "optimisationType"
                          ? row?.errMsg
                          : "\u00A0"}
                      </div>
                    }
                  >
                    <Select
                      disabled={!row?.objective}
                      value={row?.optimisationType}
                      onChange={(val) =>
                        updateObjectiveData(row.id, "optimisationType", val)
                      }
                      options={optimisationOptions}
                      placeholder="Type"
                      style={{ width: "90%" }}
                    />
                  </Form.Item>
                </Col>

                {/* Min Value */}
                <Col span={4}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={
                      row?.error && row?.errorType === "minValue" ? "error" : ""
                    }
                    // single slot for error OR warning; height always reserved
                    help={
                        <Tooltip
                          title={
                            row?.error && row?.errorType === "minValue"
                              ? row?.errMsg
                              : minWarn
                              ? `${t("extrapolation.objective.warningMinRecommend")} ${convertValue(
                                  tewntypercentPlusMinusRange(row?.startRange, row?.endRange).min
                                )}`
                              : ""
                          }
                        >
                          <div
                            style={{
                              fontSize: 10,
                              marginTop: 2,
                              height: 20,
                              lineHeight: "20px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "default", // shows normal pointer
                              color:
                                (row?.error && row?.errorType === "minValue") || minWarn
                                  ? "red"
                                  : "transparent",
                            }}
                          >
                            {row?.error && row?.errorType === "minValue"
                              ? row?.errMsg
                              : minWarn
                              ? `${t("extrapolation.objective.warningMinRecommend")} ${convertValue(
                                  tewntypercentPlusMinusRange(row?.startRange, row?.endRange).min
                                )}`
                              : "\u00A0"}
                          </div>
                        </Tooltip>
                      }
                  >
                    <InputNumber
                      formatter={formatter}
                      parser={parser}
                      onChange={(val) =>
                        updateObjectiveData(row.id, "minValue", val)
                      }
                      onBlur={() => handleBlur(row, "minValue")}
                      value={row?.minValue}
                      disabled={
                        !row?.objective ||
                        !row?.optimisationType ||
                        row?.optimisationType === "Maximize" ||
                        row?.optimisationType === "Minimize"
                      }
                      style={{ width: "95%" }}
                    />
                  </Form.Item>
                </Col>

                {/* Max Value */}
                <Col span={4}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={
                      row?.error && row?.errorType === "maxValue" ? "error" : ""
                    }
                    help={
                        <Tooltip
                          title={
                            row?.error && row?.errorType === "maxValue"
                              ? row?.errMsg
                              : maxWarn
                              ? `${t("extrapolation.objective.warningMaxRecommend")} ${convertValue(
                                  tewntypercentPlusMinusRange(row?.startRange, row?.endRange).max
                                )}`
                              : ""
                          }
                        >
                          <div
                            style={{
                              fontSize: 10,
                              marginTop: 2,
                              height: 20,
                              lineHeight: "20px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "default",
                              color:
                                (row?.error && row?.errorType === "maxValue") || maxWarn
                                  ? "red"
                                  : "transparent",
                            }}
                          >
                            {row?.error && row?.errorType === "maxValue"
                              ? row?.errMsg
                              : maxWarn
                              ? `${t("extrapolation.objective.warningMaxRecommend")} ${convertValue(
                                  tewntypercentPlusMinusRange(row?.startRange, row?.endRange).max
                                )}`
                              : "\u00A0"}
                          </div>
                        </Tooltip>
                      }
                  >
                    <InputNumber
                      formatter={formatter}
                      parser={parser}
                      onChange={(val) =>
                        updateObjectiveData(row.id, "maxValue", val)
                      }
                      onBlur={() => handleBlur(row, "maxValue")}
                      value={row?.maxValue}
                      disabled={
                        !row?.objective ||
                        !row?.optimisationType ||
                        row?.optimisationType === "Maximize" ||
                        row?.optimisationType === "Minimize"
                      }
                      style={{ width: "95%" }}
                    />
                  </Form.Item>
                </Col>

                {/* Priority */}
                <Col span={3}>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={
                      row?.error && row?.errorType === "priority" ? "error" : ""
                    }
                    help={
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          minHeight: 16,
                          color:
                            row?.error && row?.errorType === "priority"
                              ? "red"
                              : "transparent",
                        }}
                      >
                        {row?.error && row?.errorType === "priority"
                          ? row?.errMsg
                          : "\u00A0"}
                      </div>
                    }
                  >
                    <Select
                      disabled={
                        !row?.objective ||
                        !row?.optimisationType ||
                        row?.optimisationType === "Range"
                      }
                      value={row?.priority}
                      onChange={(val) =>
                        updateObjectiveData(row.id, "priority", val)
                      }
                      options={priorityOptions}
                      placeholder="Type"
                      style={{ width: "95%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            );
          })}
        </div>
      </Space>
    );

}

export default Objective;